import { Skeleton, Stack, Tooltip } from '@mui/material';
import MUITable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import Button from '../../shared/Button/Button';
import StyledTableColumnHeader from '../../shared/Table/StyledTableColumnHeader';
import Text from '../../shared/Text/Text';
import Modal from '../../shared/Modal/Modal';

import AuthContext from '../../../contexes/AuthContext';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { deleteKnowledge } from '../../../services/knowledgeService';
import { DeleteOutline, EditOutlined } from '@mui/icons-material';
import { ResponseObject } from '../../../interfaces/App';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useTranslation } from '../../../hooks/useTranslation';
import { useKnowledgesWithAttachment } from '../../../hooks/queries/useKnowledgesWithAttachment';

const Table = (): JSX.Element => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const displayMessage = useSnackBar();
  const { track } = useAnalytics();
  const queryClient = useQueryClient();
  const actionIconButtonStyle = {
    minWidth: '44px',
    width: '44px',
    height: '44px',
    borderRadius: '10px',
    background: '#F1F5F9',
    borderColor: 'rgba(15, 23, 42, 0.06)',
    color: '#1841ff',
    padding: '0.5rem',
  };

  // Use React Query to fetch knowledges with attachment status
  const {
    data: items,
    isLoading,
    error,
  } = useKnowledgesWithAttachment(currentUser?.id, !!currentUser);

  const navigateToEditKnowledge = (id: string) => {
    navigate(`/knowledges/edit/${id}`);
  };

  const handleDeleteKnowledge = async (knowledgeId: string) => {
    track(AnalyticsEvents.KNOWLEDGE_DELETION_ATTEMPTED, { knowledgeId });
    try {
      const result: ResponseObject = await deleteKnowledge(knowledgeId);
      if (result.status === 'success') {
        // Invalidate all knowledge-related queries to trigger refetch and update UI
        queryClient.invalidateQueries({
          queryKey: ['knowledges'],
          refetchType: 'active',
        });
        queryClient.invalidateQueries({
          queryKey: ['knowledges-with-attachment'],
          refetchType: 'active',
        });
        displayMessage(result.message, 'success');
        track(AnalyticsEvents.KNOWLEDGE_DELETION_SUCCESS, { knowledgeId });
      } else {
        displayMessage(result.message, 'error');
        track(AnalyticsEvents.KNOWLEDGE_DELETION_FAILURE, {
          knowledgeId,
          reason: result.message,
        });
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        displayMessage(err.message, 'error');
      }
    }
  };

  // Handle errors
  useEffect(() => {
    if (error) {
      console.error(error);
      displayMessage(
        t('Something went wrong, please reload the page.'),
        'error'
      );
    }
  }, [error, displayMessage, t]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  if (isLoading)
    return (
      <Stack spacing={1}>
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={60} />
      </Stack>
    );

  if (items.length === 0) {
    return (
      <Stack textAlign="center">
        <Text type="subtitle2">{t('No knowledges found.')}</Text>
      </Stack>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: '100%',
        overflowX: 'auto',
        borderRadius: 3,
        border: '1px solid rgba(17, 24, 39, 0.08)',
        boxShadow: '0 10px 28px rgba(15, 23, 42, 0.06)',
        bgcolor: '#FFFFFF',
      }}
    >
      <MUITable
        sx={{
          width: '100%',
          tableLayout: 'auto',
          '& .MuiTableHead-root .MuiTableRow-root': {
            bgcolor: '#FFFFFF',
          },
          '& .MuiTableHead-root .MuiTableCell-root': {
            color: '#5A6FAF',
            fontSize: '0.78rem',
            fontWeight: 700,
            textTransform: 'none',
            letterSpacing: '0.01em',
            borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
            py: 1.5,
          },
          '& .MuiTableBody-root .MuiTableRow-root': {
            transition: 'background-color 120ms ease',
            '&:hover': {
              bgcolor: '#F9FBFF',
            },
          },
          '& .MuiTableBody-root .MuiTableCell-root': {
            borderBottom: '1px solid rgba(17, 24, 39, 0.06)',
            py: 1.25,
          },
        }}
        aria-label={t('Knowledges table')}
      >
        <TableHead>
          <TableRow>
            <StyledTableColumnHeader transform="uppercase" fontWeight="bold">
              {t('Name')}
            </StyledTableColumnHeader>
            <StyledTableColumnHeader transform="uppercase" align="right">
              {/* Intentionally left blank. */}
            </StyledTableColumnHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <TableRow
              key={item.id}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component="th" scope="row">
                {item.name}
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Tooltip
                    title={t('Edit attributes')}
                    placement="right-start"
                    arrow
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        padding="0.5rem"
                        style={actionIconButtonStyle}
                        onClick={() =>
                          navigateToEditKnowledge(item.id as string)
                        }
                      >
                        <EditOutlined />
                      </Button>
                    </span>
                  </Tooltip>
                  {/* Knowledge object is attached to an agent */}
                  {item.isAttached && (
                    <Modal
                      item={item}
                      mainPromptText={t(
                        'Knowledge {{name}} cannot be deleted because it is currently attached to an agent.',
                        { name: item.name }
                      )}
                      denyText={t('Okay')}
                      description={t(
                        'If you really want to delete this knowledge, you must assign a new knowledge to the agent this knowledge is currently attached.'
                      )}
                      openModalButtonText=""
                      openModalIcon={<DeleteOutline />}
                      toolTipText={t('Delete')}
                    />
                  )}
                  {/* Knowledge object is unattached to an agent */}
                  {!item.isAttached && (
                    <Modal
                      item={item}
                      callback={handleDeleteKnowledge}
                      mainPromptText={t(
                        'Do you really want to delete knowledge {{name}}?',
                        { name: item.name }
                      )}
                      confirmText={t('Yes, Delete')}
                      denyText={t('Cancel')}
                      openModalButtonText=""
                      openModalIcon={<DeleteOutline />}
                      toolTipText={t('Delete')}
                    />
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </MUITable>
    </TableContainer>
  );
};

export default Table;
